import { canonicalForPath, descriptionForPath, OG_IMAGE, titleForPath } from './canonicals';

export type RouteMeta = {
  /** Null leaves the current document title alone. */
  title: string | null;
  /** Null leaves the current meta description alone. */
  description: string | null;
  /** Absolute canonical URL. Null means this path should not advertise a canonical. */
  canonical: string | null;
};

const page = (pathname: string): RouteMeta => ({
  title: titleForPath(pathname),
  description: descriptionForPath(pathname),
  canonical: canonicalForPath(pathname),
});

const PUBLIC_PAGES: Record<string, RouteMeta> = {
  '/': page('/'),
  '/pricing/': page('/pricing/'),
  '/privacy/': page('/privacy/'),
  '/changelog/': page('/changelog/'),
  '/terms_of_service/': page('/terms_of_service/'),
  '/legal/terms-of-service': page('/legal/terms-of-service'),
  '/account-deletion/': page('/account-deletion/'),
  '/signin/': page('/signin/'),
  '/signup/': page('/signup/'),
  '/auth/callback/': page('/auth/callback/'),
  '/billing/success': page('/billing/success'),
  '/billing/cancel': page('/billing/cancel'),
  '/password_reset/': page('/password_reset/'),
  '/password_reset_confirmation/': page('/password_reset_confirmation/'),
  '/set_password/': page('/set_password/'),
  '/account/': page('/account/'),
  '/document_storage': page('/document_storage'),
  '/feedback/': page('/feedback/'),
  '/analytics/': page('/analytics/'),
};

/** Title and canonical for a path. Signed-in `/` keeps the app title. */
export function metaForPath(pathname: string, authenticated: boolean): RouteMeta | null {
  if (pathname === '/' && authenticated) {
    return { title: 'Hesychia', description: null, canonical: canonicalForPath('/') };
  }
  const known = PUBLIC_PAGES[pathname];
  if (known) return known;
  const canonical = canonicalForPath(pathname);
  if (!canonical) return null;
  return { title: null, description: null, canonical };
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attr}="${key}"]`;
  const current = document.querySelector<HTMLMetaElement>(selector);
  const el = current ?? document.createElement('meta');
  el.setAttribute(attr, key);
  el.content = content;
  if (!current) document.head.appendChild(el);
}

export function applyRouteMeta(meta: RouteMeta | null): void {
  if (!meta || typeof document === 'undefined') return;

  if (meta.title) {
    document.title = meta.title;
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('name', 'twitter:title', meta.title);
  }

  if (meta.description) {
    const current = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const description = current ?? document.createElement('meta');
    description.name = 'description';
    description.content = meta.description;
    if (!current) document.head.appendChild(description);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('name', 'twitter:description', meta.description);
  }

  upsertMeta('property', 'og:image', OG_IMAGE);
  upsertMeta('name', 'twitter:image', OG_IMAGE);
  upsertMeta('name', 'twitter:card', 'summary_large_image');

  const existing = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!meta.canonical) {
    existing?.parentNode?.removeChild(existing);
    document.querySelector('meta[property="og:url"]')?.remove();
    return;
  }

  const link = existing ?? document.createElement('link');
  link.rel = 'canonical';
  link.href = meta.canonical;
  if (!existing) {
    document.head.appendChild(link);
  }
  upsertMeta('property', 'og:url', meta.canonical);
}
